import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './MyTeamsPage.css'; // share styles with traditional MyTeamsPage
import '../pages/DraftFreeAgency.css'; // FA styles
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { FUNCTIONS_URL } from '../services/supabaseConfig';
import BoxScore from '../components/BoxScore';
import DraftSheetPage from './DraftSheetPage';
import FreeAgencyPage from './FreeAgencyPage';

// ──────────── Types ────────────
interface TeamFromDB {
  id: string; name: string; city: string; abbreviation: string;
  tier: number; division: string; conference: string;
  wins: number; losses: number; runs_scored?: number; runs_allowed?: number;
  is_ai?: boolean; owner_id?: string; league_id?: string;
  primary_color?: string; secondary_color?: string; uniform_style?: string;
  strategy?: any; batting_order?: string[]; rotation_order?: string[];
  depth_chart?: Record<string, string[]>;
}

interface PlayerFromDB {
  id: string; first_name: string; last_name: string; position: string;
  overall: number; is_hitter: boolean;
  power: number; contact: number; speed: number; discipline: number;
  fielding: number; arm: number; endurance: number;
  velocity: number; control: number; movement: number;
  current_stamina: number;
}

interface ScheduleGame {
  id: string; game_id?: string; game_day: number;
  home_team_id: string; away_team_id: string;
  home_score: number | null; away_score: number | null; status: string;
  home_team?: { city: string; name: string };
  away_team?: { city: string; name: string };
  win_pitcher?: { id: string; name: string };
  loss_pitcher?: { id: string; name: string };
  save_pitcher?: { id: string; name: string };
}

interface StrategySettings {
  hittingApproach: number; stealAttempts: number; baseRunningAggression: number;
  sacrificeBunts: number; intentionalWalks: number;
  pitcherManagement: number; shiftFrequency: number; bullpenUsage: number;
  defenseFirst: number; lineupProtection: number;
}

interface UniformSettings {
  primaryColor: string; secondaryColor: string;
  style: 'pinstripe' | 'solid' | 'gradient' | 'v-neck' | 'classic';
  capColor: string; jerseyNumber: string;
}

const DEFAULT_STRATEGY: StrategySettings = {
  hittingApproach: 50, stealAttempts: 50, baseRunningAggression: 50,
  sacrificeBunts: 50, intentionalWalks: 50,
  pitcherManagement: 50, shiftFrequency: 50, bullpenUsage: 50,
  defenseFirst: 50, lineupProtection: 50,
};

const DEFAULT_UNIFORM: UniformSettings = {
  primaryColor: '#1a3a5c', secondaryColor: '#c4a35a',
  style: 'pinstripe', capColor: '#1a3a5c', jerseyNumber: '00',
};

const UNIFORM_STYLES = [
  { value: 'pinstripe', label: 'Pinstripe', desc: 'Classic thin stripes' },
  { value: 'solid', label: 'Solid', desc: 'Clean solid color' },
  { value: 'gradient', label: 'Gradient', desc: 'Fading two-tone' },
  { value: 'v-neck', label: 'V-Neck', desc: 'Contrast V-neck collar' },
  { value: 'classic', label: 'Classic', desc: 'Traditional button-down' },
];

const TEAM_COLORS = [
  '#1a3a5c', '#c41230', '#0c2340', '#003831', '#ce1141',
  '#e81828', '#002d72', '#27251f', '#e31937', '#004687',
  '#002b5c', '#bd3039', '#0c2340', '#333366', '#c41e3a',
  '#fb4f14', '#003263', '#ff5910', '#002d62', '#ab0003',
  '#572d2d', '#2f2f2f', '#4a2c7a', '#006bb6', '#134a8e',
];

const POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];
const BATTING_SLOTS = 9;

type TeamTab = 'roster' | 'lineup' | 'depth' | 'rotation' | 'playerStats' | 'schedule'
  | 'strategy' | 'uniforms' | 'draft-sheet' | 'fa';

interface QuickLeagueTeamPageProps {
  onNavigate?: (route: string) => void;
  userData?: any;
}

// ──────────── Helpers ────────────
function getGrade(val: number): string {
  if (val >= 70) return 'A+'; if (val >= 65) return 'A'; if (val >= 60) return 'A-';
  if (val >= 57) return 'B+'; if (val >= 53) return 'B'; if (val >= 50) return 'B-';
  if (val >= 47) return 'C+'; if (val >= 43) return 'C'; if (val >= 40) return 'C-';
  if (val >= 35) return 'D+'; if (val >= 30) return 'D'; if (val >= 25) return 'D-';
  return 'F';
}
function getAttrColor(val: number): string {
  if (val >= 65) return '#00d4aa'; if (val >= 50) return '#3b82f6';
  if (val >= 35) return '#f59e0b'; return '#ef4444';
}

// ──────────── Component ────────────
const QuickLeagueTeamPage: React.FC<QuickLeagueTeamPageProps> = ({ onNavigate, userData }) => {
  const { leagueId, teamId } = useParams<{ leagueId: string; teamId: string }>();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TeamTab>('roster');
  const [leagueInfo, setLeagueInfo] = useState<any>(null);
  const [team, setTeam] = useState<TeamFromDB | null>(null);
  const [roster, setRoster] = useState<PlayerFromDB[]>([]);
  const [leagueStatus, setLeagueStatus] = useState<string>('');

  // Lineup / Rotation / Depth Chart / Strategy / Uniforms
  const [lineup, setLineup] = useState<string[]>([]);
  const [pitchingRotation, setPitchingRotation] = useState<string[]>([]);
  const [depthChart, setDepthChart] = useState<Record<string, (string | null)[]>>({});
  const [strategy, setStrategy] = useState<StrategySettings>(DEFAULT_STRATEGY);
  const [uniform, setUniform] = useState<UniformSettings>(DEFAULT_UNIFORM);

  // Schedule
  const [schedule, setSchedule] = useState<ScheduleGame[]>([]);

  // Player stats
  const [playerStats, setPlayerStats] = useState<Map<string, any>>(new Map());

  // Roster sort
  const [hitterSort, setHitterSort] = useState<{key: string; dir: 'asc'|'desc'}>({key: 'overall', dir: 'desc'});
  const [pitcherSort, setPitcherSort] = useState<{key: string; dir: 'asc'|'desc'}>({key: 'overall', dir: 'desc'});

  // BoxScore modal
  const [boxscoreGameId, setBoxscoreGameId] = useState<string | null>(null);

  // Team standing from standings table
  const [teamStanding, setTeamStanding] = useState<{wins: number; losses: number; runs_scored: number; runs_allowed: number} | null>(null);

  // ──────────── Load ────────────
  useEffect(() => {
    if (leagueId && teamId) loadAll();
  }, [leagueId, teamId]);

  async function loadAll() {
    setLoading(true);
    if (!isSupabaseConfigured() || !leagueId || !teamId) { setLoading(false); return; }

    // League info
    const { data: league } = await supabase.from('leagues').select('*').eq('id', leagueId).single();
    if (league) {
      setLeagueInfo(league);
      setLeagueStatus(league.status);
    }

    // Team
    const { data: teamData } = await supabase.from('teams').select('*').eq('id', teamId).single();
    if (teamData) setTeam(teamData);

    // Roster
    const { data: players } = await supabase
      .from('players').select('*').eq('team_id', teamId).order('overall', { ascending: false });
    if (players) {
      setRoster(players);

      // Default lineup / rotation if not set
      if (!teamData?.batting_order?.length) {
        const hitters = players.filter((p: PlayerFromDB) => p.is_hitter).sort((a:any,b:any) => b.overall - a.overall);
        const pitchers = players.filter((p: PlayerFromDB) => !p.is_hitter).sort((a:any,b:any) => b.overall - a.overall);
        setLineup(hitters.slice(0, BATTING_SLOTS).map((p: any) => p.id));
        setPitchingRotation(pitchers.map((p: any) => p.id));
      }
    }

    // Standings
    const seasonYear = league?.season_year || new Date().getFullYear();
    const { data: standings } = await supabase
      .from('league_standings').select('wins, losses, runs_scored, runs_allowed')
      .eq('team_id', teamId).eq('season_year', seasonYear).single();
    if (standings) setTeamStanding(standings);

    // Players stats
    if (players?.length) {
      const playerIds = players.map((p: PlayerFromDB) => p.id);
      const statsMap = new Map<string, any>();
      for (let i = 0; i < playerIds.length; i += 100) {
        const batch = playerIds.slice(i, i + 100);
        const { data: statsData } = await supabase
          .from('player_season_stats').select('*').in('player_id', batch).eq('season_year', seasonYear);
        if (statsData) statsData.forEach((s: any) => statsMap.set(s.player_id, s));
      }
      setPlayerStats(statsMap);
    }

    // Strategy
    if (teamData?.strategy) {
      setStrategy({ ...DEFAULT_STRATEGY, ...teamData.strategy });
    } else {
      setStrategy(DEFAULT_STRATEGY);
    }

    // Uniform
    if (teamData?.primary_color) {
      setUniform({
        primaryColor: teamData.primary_color,
        secondaryColor: teamData.secondary_color || '#ffffff',
        style: (teamData.uniform_style as any) || 'solid',
        capColor: teamData.primary_color,
        jerseyNumber: '00',
      });
    }

    // Lineup / rotation / depth from saved team data
    if (teamData?.batting_order?.length) setLineup(teamData.batting_order);
    if (teamData?.rotation_order?.length) setPitchingRotation(teamData.rotation_order);

    if (teamData?.depth_chart && typeof teamData.depth_chart === 'object') {
      const dc: Record<string, (string | null)[]> = {};
      POSITIONS.forEach(pos => {
        const saved = (teamData.depth_chart as Record<string, string[]>)[pos] || [];
        dc[pos] = [saved[0] || null, saved[1] || null, saved[2] || null];
      });
      for (let i = 1; i <= 11; i++) {
        const key = `P${i}`;
        const saved = (teamData.depth_chart as Record<string, string[]>)[key] || [];
        dc[key] = [saved[0] || null, null, null];
      }
      setDepthChart(dc);
    } else if (players) {
      // Auto-generate depth chart
      const dc: Record<string, (string | null)[]> = {};
      POSITIONS.forEach(pos => { dc[pos] = [null, null, null]; });
      for (let i = 1; i <= 11; i++) { dc[`P${i}`] = [null, null, null]; }
      const hitters = players.filter((p: PlayerFromDB) => p.is_hitter);
      const pitchers2 = players.filter((p: PlayerFromDB) => !p.is_hitter);
      POSITIONS.forEach(pos => {
        const candidates = hitters.filter((p: PlayerFromDB) => p.position === pos).sort((a:any,b:any) => b.overall - a.overall);
        dc[pos] = [candidates[0]?.id || null, candidates[1]?.id || null, candidates[2]?.id || null];
      });
      const sortedP = pitchers2.sort((a:any,b:any) => b.overall - a.overall);
      sortedP.forEach((p: PlayerFromDB, idx: number) => {
        if (idx < 11) dc[`P${idx + 1}`] = [p.id, null, null];
      });
      setDepthChart(dc);
    }

    // Schedule
    const { data: games } = await supabase
      .from('league_schedule')
      .select('*').or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`)
      .order('game_day', { ascending: true }).limit(50);
    if (games?.length) {
      const oppIds = new Set(games.map((g: any) => g.home_team_id === teamId ? g.away_team_id : g.home_team_id));
      const { data: oppTeams } = await supabase.from('teams').select('id, city, name').in('id', Array.from(oppIds));
      const oppMap = new Map<string, any>();
      (oppTeams || []).forEach((t: any) => oppMap.set(t.id, t));

      const completedIds = games.filter((g: any) => g.status === 'completed').map((g: any) => g.id);
      let pitcherMap = new Map<string, any>();
      if (completedIds.length) {
        const { data: pStats } = await supabase.from('player_game_stats')
          .select('game_id, player_id, win, loss, save')
          .in('game_id', completedIds).or('win.eq.true,loss.eq.true,save.eq.true');
        if (pStats) {
          const pIds = Array.from(new Set(pStats.map((s: any) => s.player_id)));
          const { data: pNames } = await supabase.from('players').select('id, first_name, last_name').in('id', pIds);
          const nameMap = new Map<string, string>();
          (pNames || []).forEach((p: any) => nameMap.set(p.id, `${p.first_name} ${p.last_name}`));
          pStats.forEach((s: any) => {
            const k = `${s.game_id}-${s.player_id}`;
            pitcherMap.set(k, { ...s, name: nameMap.get(s.player_id) || 'Unknown' });
          });
        }
      }

      const enriched = games.map((g: any) => {
        const ent = Array.from(pitcherMap.values()).filter((p: any) => p.game_id === g.id);
        const oppId = g.home_team_id === teamId ? g.away_team_id : g.home_team_id;
        return {
          ...g, opp_team: oppMap.get(oppId) || { city: '', name: 'Unknown' }, opp_id: oppId,
          win_pitcher: ent.find((p: any) => p.win),
          loss_pitcher: ent.find((p: any) => p.loss),
          save_pitcher: ent.find((p: any) => p.save),
        };
      });
      setSchedule(enriched);
    }

    setLoading(false);
  }

  function getPlayer(id: string): PlayerFromDB | undefined { return roster.find(p => p.id === id); }

  // ──────────── Tab renders ────────────
  const renderRoster = () => {
    const hitters = roster.filter(p => p.is_hitter);
    const pitchers = roster.filter(p => !p.is_hitter);
    const POSITION_ORDER: Record<string, number> = { C: 1, '1B': 2, '2B': 3, SS: 4, '3B': 5, LF: 6, CF: 7, RF: 8 };
    const sortPlayers = (pls: PlayerFromDB[], sort: {key: string; dir: 'asc'|'desc'}) => {
      const sorted = [...pls];
      sorted.sort((a: any, b: any) => {
        let aV = a[sort.key] ?? 0, bV = b[sort.key] ?? 0;
        if (sort.key === 'position') { aV = POSITION_ORDER[aV] ?? 99; bV = POSITION_ORDER[bV] ?? 99; }
        if (typeof aV === 'string') return sort.dir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        return sort.dir === 'asc' ? aV - bV : bV - aV;
      });
      return sorted;
    };
    const toggleSort = (cur: {key: string; dir: 'asc'|'desc'}, key: string, setter: Function) => {
      if (cur.key === key) setter({key, dir: cur.dir === 'desc' ? 'asc' : 'desc'});
      else setter({key, dir: 'desc'});
    };
    const sortArrow = (s: {key: string; dir: 'asc'|'desc'}, key: string) => s.key !== key ? ' ⇅' : s.dir === 'desc' ? ' ▼' : ' ▲';
    const sortedH = sortPlayers(hitters, hitterSort);
    const sortedP = sortPlayers(pitchers, pitcherSort);

    return (
      <div className="roster-tab">
        <div className="roster-actions-bar">
          <span className="roster-count-label">Roster: {roster.length}/25</span>
        </div>
        <div className="roster-section">
          <h3>Hitters ({hitters.length})</h3>
          <table className="roster-table"><thead><tr>
            <th>Name</th>
            <th className="sortable-th" onClick={() => toggleSort(hitterSort, 'position', setHitterSort)}>Pos{sortArrow(hitterSort, 'position')}</th>
            <th className="sortable-th" onClick={() => toggleSort(hitterSort, 'overall', setHitterSort)}>OVR{sortArrow(hitterSort, 'overall')}</th>
            <th className="sortable-th" onClick={() => toggleSort(hitterSort, 'power', setHitterSort)}>PWR{sortArrow(hitterSort, 'power')}</th>
            <th className="sortable-th" onClick={() => toggleSort(hitterSort, 'contact', setHitterSort)}>CON{sortArrow(hitterSort, 'contact')}</th>
            <th className="sortable-th" onClick={() => toggleSort(hitterSort, 'speed', setHitterSort)}>SPD{sortArrow(hitterSort, 'speed')}</th>
            <th className="sortable-th" onClick={() => toggleSort(hitterSort, 'fielding', setHitterSort)}>FLD{sortArrow(hitterSort, 'fielding')}</th>
            <th>STA</th>
          </tr></thead><tbody>
            {sortedH.map(p => (
              <tr key={p.id} className="roster-row">
                <td className="player-name-cell" onClick={() => onNavigate?.('/player/' + p.id)}>{p.first_name} {p.last_name}</td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}>{p.position}</td>
                <td onClick={() => onNavigate?.('/player/' + p.id)} style={{color: getAttrColor(p.overall)}}>{p.overall}</td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.power)}}>{getGrade(p.power)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.contact)}}>{getGrade(p.contact)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.speed)}}>{getGrade(p.speed)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.fielding)}}>{getGrade(p.fielding)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span className="stamina-mini"><span className="stamina-mini-bar"><span className="stamina-mini-fill" style={{width:`${p.current_stamina||100}%`, background:(p.current_stamina||100)>=60?'#00d4aa':(p.current_stamina||100)>=35?'#f59e0b':'#ef4444'}}/></span>{p.current_stamina||100}</span></td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <div className="roster-section">
          <h3>Pitchers ({pitchers.length})</h3>
          <table className="roster-table"><thead><tr>
            <th>Name</th>
            <th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'position', setPitcherSort)}>Role{sortArrow(pitcherSort, 'position')}</th>
            <th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'overall', setPitcherSort)}>OVR{sortArrow(pitcherSort, 'overall')}</th>
            <th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'velocity', setPitcherSort)}>VEL{sortArrow(pitcherSort, 'velocity')}</th>
            <th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'control', setPitcherSort)}>CTL{sortArrow(pitcherSort, 'control')}</th>
            <th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'movement', setPitcherSort)}>MOV{sortArrow(pitcherSort, 'movement')}</th>
            <th>STA</th>
          </tr></thead><tbody>
            {sortedP.map(p => (
              <tr key={p.id} className="roster-row">
                <td className="player-name-cell" onClick={() => onNavigate?.('/player/' + p.id)}>{p.first_name} {p.last_name}</td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}>{p.position}</td>
                <td onClick={() => onNavigate?.('/player/' + p.id)} style={{color: getAttrColor(p.overall)}}>{p.overall}</td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.velocity)}}>{getGrade(p.velocity)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.control)}}>{getGrade(p.control)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{color: getAttrColor(p.movement)}}>{getGrade(p.movement)}</span></td>
                <td onClick={() => onNavigate?.('/player/' + p.id)}><span className="stamina-mini"><span className="stamina-mini-bar"><span className="stamina-mini-fill" style={{width:`${p.current_stamina||100}%`, background:(p.current_stamina||100)>=60?'#00d4aa':(p.current_stamina||100)>=35?'#f59e0b':'#ef4444'}}/></span>{p.current_stamina||100}</span></td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>
    );
  };

  const renderLineup = () => (
    <div className="lineup-tab">
      <div className="lineup-section">
        <h3>Batting Order</h3>
        <table className="lineup-table"><thead><tr><th>#</th><th></th><th>Player</th><th>Pos</th><th>OVR</th></tr></thead><tbody>
          {lineup.map((pid, idx) => {
            const p = getPlayer(pid); if (!p) return null;
            return (<tr key={pid}>
              <td className="order-num">{idx + 1}</td>
              <td className="reorder-cell">
                {idx > 0 && <button className="reorder-btn" onClick={()=>{const nl=[...lineup];[nl[idx-1],nl[idx]]=[nl[idx],nl[idx-1]];setLineup(nl);}}>▲</button>}
                {idx < lineup.length-1 && <button className="reorder-btn" onClick={()=>{const nl=[...lineup];[nl[idx],nl[idx+1]]=[nl[idx+1],nl[idx]];setLineup(nl);}}>▼</button>}
              </td>
              <td className="player-name-cell" onClick={()=>onNavigate?.('/player/'+p.id)}>{p.first_name} {p.last_name}</td>
              <td>{p.position}</td>
              <td style={{color:getAttrColor(p.overall)}}>{p.overall}</td>
            </tr>);
          })}
        </tbody></table>
      </div>
      <div className="lineup-section">
        <h3>Pitching Rotation</h3>
        <table className="lineup-table"><thead><tr><th>#</th><th></th><th>Player</th><th>OVR</th><th>VEL</th><th>CTL</th><th>MOV</th></tr></thead><tbody>
          {pitchingRotation.map((pid, idx) => {
            const p = getPlayer(pid); if (!p) return null;
            return (<tr key={pid}>
              <td className="order-num">P{idx + 1}</td>
              <td className="reorder-cell">
                {idx > 0 && <button className="reorder-btn" onClick={()=>{const pr=[...pitchingRotation];[pr[idx-1],pr[idx]]=[pr[idx],pr[idx-1]];setPitchingRotation(pr);}}>▲</button>}
                {idx < pitchingRotation.length-1 && <button className="reorder-btn" onClick={()=>{const pr=[...pitchingRotation];[pr[idx],pr[idx+1]]=[pr[idx+1],pr[idx]];setPitchingRotation(pr);}}>▼</button>}
              </td>
              <td className="player-name-cell" onClick={()=>onNavigate?.('/player/'+p.id)}>{p.first_name} {p.last_name}</td>
              <td style={{color:getAttrColor(p.overall)}}>{p.overall}</td>
              <td><span style={{color:getAttrColor(p.velocity)}}>{getGrade(p.velocity)}</span></td>
              <td><span style={{color:getAttrColor(p.control)}}>{getGrade(p.control)}</span></td>
              <td><span style={{color:getAttrColor(p.movement)}}>{getGrade(p.movement)}</span></td>
            </tr>);
          })}
        </tbody></table>
        <button className="save-btn" style={{marginTop:12}} onClick={async () => {
          if (!team || !isSupabaseConfigured()) return;
          const dcIds: Record<string, string[]> = {};
          Object.keys(depthChart).forEach(pos => { dcIds[pos] = (depthChart[pos]||[]).filter((id):id is string=>id!==null); });
          await supabase.from('teams').update({
            rotation_order: pitchingRotation.slice(0, 11),
            batting_order: lineup,
            depth_chart: dcIds,
          }).eq('id', teamId);
          alert('✅ Lineup, rotation, and depth chart saved!');
        }}>💾 Save Lineup, Rotation & Depth Chart</button>
      </div>
    </div>
  );

  const renderDepthChart = () => (
    <div className="lineup-tab">
      <div className="lineup-section">
        <h3>Depth Chart — Position Players</h3>
        <p className="depth-chart-note">Set 3-deep depth per position. Backups replace tired starters automatically.</p>
        <table className="depth-chart-table dc-dropdown-table"><thead><tr><th>Pos</th><th>1st</th><th>2nd</th><th>3rd</th></tr></thead><tbody>
          {POSITIONS.map(pos => {
            const eligible = roster.filter(p => p.is_hitter && p.position === pos);
            const utility = roster.filter(p => p.is_hitter && p.position !== pos);
            const allOpts = [...eligible, ...utility].filter((p, i, a) => a.findIndex(x => x.id === p.id) === i).sort((a, b) => b.overall - a.overall);
            const slotIds = depthChart[pos] || [null, null, null];
            const getAvail = (si: number) => {
              const taken = slotIds.filter((id, i) => i !== si && id !== null) as string[];
              return allOpts.filter(p => !taken.includes(p.id));
            };
            return (<tr key={pos} className="dc-hitter-row">
              <td className="pos-cell dc-pos-cell">{pos}</td>
              {[0,1,2].map(si => {
                const avail = getAvail(si);
                return (<td key={si} className="dc-slot-cell">
                  <select className="dc-dropdown" value={slotIds[si]||''} onChange={e=>{
                    const newId = e.target.value || null;
                    setDepthChart(prev=>{const u={...prev};const s=[...((u[pos])||[null,null,null])];s[si]=newId;u[pos]=s;return u;});
                  }}>
                    <option value="">— Empty —</option>
                    {avail.map(p=>(<option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.position} {p.overall})</option>))}
                  </select>
                </td>);
              })}
            </tr>);
          })}
        </tbody></table>
      </div>
      <div className="lineup-section">
        <h3>Pitcher Rankings</h3>
        <p className="depth-chart-note">Assign pitchers by overall rank. All 11 pitchers are in the rotation.</p>
        <table className="depth-chart-table dc-dropdown-table"><thead><tr><th>Rank</th><th>Pitcher</th></tr></thead><tbody>
          {Array.from({length:11},(_,i)=>{
            const key=`P${i+1}`;
            const ROLE_LABELS: Record<number, string> = {1:'SP1',2:'SP2',3:'SP3',4:'SP4',5:'SP5',6:'LR',7:'MR1',8:'MR2',9:'SU1',10:'SU2',11:'CL'};
            const allP = roster.filter(p=>!p.is_hitter).sort((a,b)=>b.overall-a.overall);
            const sIds = depthChart[key]||[null,null,null];
            return (<tr key={key} className="dc-pitcher-row">
              <td className="pos-cell dc-pos-cell dc-role-cell">{ROLE_LABELS[i+1]}</td>
              <td className="dc-slot-cell dc-role-slot">
                <select className="dc-dropdown" value={sIds[0]||''} onChange={e=>{
                  setDepthChart(prev=>{const u={...prev};u[key]=[e.target.value||null,null,null];return u;});
                }}>
                  <option value="">— Empty —</option>
                  {allP.map(p=>(<option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.position} {p.overall})</option>))}
                </select>
              </td>
            </tr>);
          })}
        </tbody></table>
        <button className="save-btn" style={{marginTop:12}} onClick={async () => {
          if (!team || !isSupabaseConfigured()) return;
          const dcIds: Record<string, string[]> = {};
          Object.keys(depthChart).forEach(pos => { dcIds[pos] = (depthChart[pos]||[]).filter((id):id is string=>id!==null); });
          await supabase.from('teams').update({ rotation_order: pitchingRotation.slice(0,11), batting_order: lineup, depth_chart: dcIds }).eq('id', teamId);
          alert('✅ Depth chart saved!');
        }}>💾 Save Depth Chart</button>
      </div>
    </div>
  );

  // Full player stats (hitters + pitchers) — mirrors TeamDetailPage
  const [playerStatsView, setPlayerStatsView] = useState<'hitters'|'pitchers'>('hitters');
  const [playerStatsSortCol, setPlayerStatsSortCol] = useState('_ops');
  const [playerStatsSortDir, setPlayerStatsSortDir] = useState<'asc'|'desc'>('desc');
  const [playerStatsSeason, setPlayerStatsSeason] = useState<number|'current'>('current');
  const [playerStatsSeasons, setPlayerStatsSeasons] = useState<number[]>([]);

  useEffect(() => {
    const loadSeasons = async () => {
      if (!supabase || !team?.id) return;
      const { data } = await supabase.from('season_history_stats').select('season_year').eq('team_id', team.id);
      if (data) {
        const years = Array.from(new Set(data.map((d:any)=>d.season_year))).sort((a:number,b:number)=>b-a);
        setPlayerStatsSeasons(years);
      }
    };
    loadSeasons();
  }, [team?.id]);

  const isPitcherPos = (pos: string) => (pos||'').toUpperCase() === 'P';

  const fullHitters = roster
    .filter(p => !isPitcherPos(p.position))
    .map(p => {
      const s = playerStats.get(p.id);
      if (!s) return null;
      const ab = s.at_bats||0, h = s.hits||0, bb = s.walks||0;
      const singles = h - (s.doubles||0) - (s.triples||0) - (s.home_runs||0);
      const tb = singles + (s.doubles||0)*2 + (s.triples||0)*3 + (s.home_runs||0)*4;
      const pa = s.plate_appearances||(ab+bb);
      const avg = ab>0?h/ab:0, obp = pa>0?(h+bb)/pa:0, slg = ab>0?tb/ab:0;
      return {...s,player:p,_avg:avg,_ops:obp+slg,_h:h,_hr:s.home_runs||0,_rbi:s.rbi||0,_r:s.runs||0,_bb:bb,_k:s.strikeouts||0,_sb:s.stolen_bases||0,_ab:ab,_g:s.games||0};
    }).filter(Boolean) as any[];

  const fullPitchers = roster
    .filter(p => isPitcherPos(p.position))
    .map(p => {
      const s = playerStats.get(p.id);
      if (!s) return null;
      const ip = s.innings_pitched||0, er = s.earned_runs||0, bb = s.walks_allowed||0, h = s.hits_allowed||0;
      return {...s,player:p,_era:ip>0?(er*9)/ip:99.99,_ip:ip,_w:s.wins||0,_l:s.losses||0,_k:s.strikeouts_pitched||0,_bb:bb,_h:h,_whip:ip>0?(bb+h)/ip:0,_g:s.games||0,_gs:s.starts||0,_sv:s.saves||0};
    }).filter(Boolean) as any[];

  const playerStatsSorted = playerStatsView === 'hitters'
    ? [...fullHitters].sort((a:any,b:any) => playerStatsSortDir==='asc' ? (a[playerStatsSortCol]??0)-(b[playerStatsSortCol]??0) : (b[playerStatsSortCol]??0)-(a[playerStatsSortCol]??0))
    : [...fullPitchers].sort((a:any,b:any) => playerStatsSortDir==='asc' ? (a[playerStatsSortCol]??0)-(b[playerStatsSortCol]??0) : (b[playerStatsSortCol]??0)-(a[playerStatsSortCol]??0));

  const SortH: React.FC<{col:string; children:React.ReactNode}> = ({col, children}) => (
    <th className="sortable" onClick={() => { if(playerStatsSortCol===col) setPlayerStatsSortDir(d=>d==='asc'?'desc':'asc'); else { setPlayerStatsSortCol(col); setPlayerStatsSortDir(col==='_era'||col==='_whip'||col==='_bb'?'asc':'desc'); } }}>
      {children} {playerStatsSortCol===col ? (playerStatsSortDir==='asc'?'▲':'▼') : ''}
    </th>
  );

  const renderPlayerStats = () => (
    <div className="player-stats-tab">
      <div className="leaders-controls" style={{marginBottom:12}}>
        <div className="leaders-toggle">
          <button className={`ltab ${playerStatsView==='hitters'?'active':''}`} onClick={()=>{setPlayerStatsView('hitters');setPlayerStatsSortCol('_ops');setPlayerStatsSortDir('desc');}}>🏏 Hitters</button>
          <button className={`ltab ${playerStatsView==='pitchers'?'active':''}`} onClick={()=>{setPlayerStatsView('pitchers');setPlayerStatsSortCol('_era');setPlayerStatsSortDir('asc');}}>⚾ Pitchers</button>
        </div>
        {playerStatsSeasons.length>0 && (
          <select className="season-select" value={playerStatsSeason} onChange={e=>setPlayerStatsSeason(e.target.value==='current'?'current':parseInt(e.target.value))}>
            <option value="current">Current Season</option>
            {playerStatsSeasons.map(y=><option key={y} value={y}>Season {y}</option>)}
          </select>
        )}
      </div>
      <div className="leaders-table-wrap">
        {playerStatsView==='hitters' ? (
          <table className="leaders-table"><thead><tr>
            <th>#</th><th>Player</th><th>Pos</th>
            <SortH col="_g">G</SortH><SortH col="_ab">AB</SortH><SortH col="_h">H</SortH>
            <SortH col="_hr">HR</SortH><SortH col="_rbi">RBI</SortH><SortH col="_r">R</SortH>
            <SortH col="_bb">BB</SortH><SortH col="_k">K</SortH><SortH col="_sb">SB</SortH>
            <SortH col="_avg">AVG</SortH><SortH col="_ops">OPS</SortH>
          </tr></thead><tbody>
            {playerStatsSorted.map((s:any,idx:number)=>(<tr key={s.player_id}>
              <td className="rank">{idx+1}</td>
              <td className="player-name" style={{cursor:'pointer'}} onClick={()=>onNavigate?.('/player/'+s.player_id)}>{s.player.first_name} {s.player.last_name}</td>
              <td>{s.player.position}</td>
              <td>{s._g}</td><td>{s._ab}</td><td>{s._h}</td><td>{s._hr}</td><td>{s._rbi}</td><td>{s._r}</td>
              <td>{s._bb}</td><td>{s._k}</td><td>{s._sb}</td>
              <td className="stat-highlight">{s._avg.toFixed(3).replace(/^0/,'')}</td>
              <td className="stat-highlight">{s._ops.toFixed(3).replace(/^0/,'')}</td>
            </tr>))}
          </tbody></table>
        ) : (
          <table className="leaders-table"><thead><tr>
            <th>#</th><th>Player</th>
            <SortH col="_g">G</SortH><SortH col="_gs">GS</SortH>
            <SortH col="_w">W</SortH><SortH col="_l">L</SortH><SortH col="_sv">SV</SortH>
            <SortH col="_era">ERA</SortH><SortH col="_ip">IP</SortH>
            <SortH col="_h">H</SortH><SortH col="_bb">BB</SortH><SortH col="_k">K</SortH>
            <SortH col="_whip">WHIP</SortH>
          </tr></thead><tbody>
            {playerStatsSorted.map((s:any,idx:number)=>(<tr key={s.player_id}>
              <td className="rank">{idx+1}</td>
              <td className="player-name" style={{cursor:'pointer'}} onClick={()=>onNavigate?.('/player/'+s.player_id)}>{s.player.first_name} {s.player.last_name}</td>
              <td>{s._g}</td><td>{s._gs}</td>
              <td>{s._w}</td><td>{s._l}</td><td>{s._sv}</td>
              <td className="stat-highlight">{s._era<=0?'∞':s._era.toFixed(2)}</td>
              <td>{s._ip.toFixed(1)}</td>
              <td>{s._h}</td><td>{s._bb}</td><td>{s._k}</td>
              <td>{s._whip.toFixed(2)}</td>
            </tr>))}
          </tbody></table>
        )}
      </div>
    </div>
  );

  const renderSchedule2 = () => {
    let runningW = 0, runningL = 0;
    const nav = (page: string) => window.dispatchEvent(new CustomEvent('navigate', { detail: page }));
    return (
      <div className="schedule-tab">
        <table className="bh-schedule-table"><thead><tr>
          <th>Date</th><th>Opponent</th><th>Result</th><th>Record</th><th>Win</th><th>Loss</th><th>Save</th>
        </tr></thead><tbody>
          {schedule.map(game => {
            const isHome = game.home_team_id === team?.id;
            const oppTeam = (game as any).opp_team;
            const oppName = oppTeam?.city && oppTeam?.name ? `${oppTeam.city} ${oppTeam.name}` : 'TBD';
            const oppId = (game as any).opp_id || (isHome ? game.away_team_id : game.home_team_id);
            const isComp = game.status === 'completed';
            let result = '', rc = '', score = '';
            if (isComp) {
              const ms = isHome ? game.home_score : game.away_score;
              const ts = isHome ? game.away_score : game.home_score;
              const won = (ms||0) > (ts||0);
              if (won) runningW++; else runningL++;
              result = won ? 'W' : 'L'; rc = won ? 'result-w' : 'result-l'; score = `${ms}–${ts}`;
            }
            if (!isComp && !game.home_team) return (<tr key={game.id} className="off-day"><td>{/* day */}Day {game.game_day}</td><td colSpan={6} className="off-day-cell">—</td></tr>);
            return (<tr key={game.id}>
              <td className="date-cell">Day {game.game_day}</td>
              <td><span className="ha-cell">{isHome?'vs':'@'}</span> <span className="opp-link" onClick={()=>onNavigate?.(`/team/${oppId}`)}>{oppName}</span></td>
              <td>{isComp?<span className={rc}><span className="score-link" onClick={()=>setBoxscoreGameId(game.game_id||game.id)}>{result}, {score}</span></span>:'—'}</td>
              <td className="record-cell">{isComp?`${runningW}–${runningL}`:'—'}</td>
              <td>{game.win_pitcher?<span className="player-link" onClick={()=>nav('/player/'+game.win_pitcher?.id)}>{game.win_pitcher.name}</span>:'—'}</td>
              <td>{game.loss_pitcher?<span className="player-link" onClick={()=>nav('/player/'+game.loss_pitcher?.id)}>{game.loss_pitcher.name}</span>:'—'}</td>
              <td>{game.save_pitcher?<span className="player-link" onClick={()=>nav('/player/'+game.save_pitcher?.id)}>{game.save_pitcher.name}</span>:'—'}</td>
            </tr>);
          })}
          {schedule.length===0&&<tr><td colSpan={7} className="empty-cell">No games scheduled.</td></tr>}
        </tbody></table>
      </div>
    );
  };

  const renderStrategy = () => {
    const sliderGroups = [
      { group: '⚔️ Offense', items: [
        { key: 'hittingApproach', label: 'Hitting Approach', left: 'Small Ball', right: 'Swing for Power', desc: 'Contact & singles vs swinging for extra bases.' },
        { key: 'stealAttempts', label: 'Steal Attempts', left: 'Rare', right: 'Run Wild', desc: 'How aggressively runners attempt to steal.' },
        { key: 'baseRunningAggression', label: 'Base Running', left: 'Conservative', right: 'Aggressive', desc: 'How often runners take extra bases on hits.' },
        { key: 'sacrificeBunts', label: 'Sacrifice Bunts', left: 'Never', right: 'Often', desc: 'How often to bunt runners into scoring position.' },
        { key: 'intentionalWalks', label: 'Intentional Walks', left: 'Never', right: 'Pitch Around', desc: 'How often to put dangerous hitters on base.' },
      ]},
      { group: '🛡️ Pitching & Defense', items: [
        { key: 'pitcherManagement', label: 'Pitcher Hook', left: 'Quick Hook', right: 'Long Leash', desc: 'How quickly you pull struggling starters.' },
        { key: 'bullpenUsage', label: 'Bullpen Usage', left: 'Save for 9th', right: 'Best Arm Now', desc: 'When to use your best relievers.' },
        { key: 'shiftFrequency', label: 'Defensive Shift', left: 'Standard', right: 'Shift Heavy', desc: 'How often to overload the pull side.' },
      ]},
      { group: '📋 Lineup Philosophy', items: [
        { key: 'defenseFirst', label: 'Defense Priority', left: 'Bat First', right: 'Defense First', desc: 'Prioritize fielding over offense in lineup.' },
        { key: 'lineupProtection', label: 'Lineup Protection', left: 'Spread Out', right: 'Cluster Stars', desc: 'Concentrate best hitters vs spread production.' },
      ]},
    ];
    return (<div className="strategy-tab">
      <h3>Team Strategy</h3>
      <p className="section-note">These sliders directly affect how your team plays in every simulated game.</p>
      {sliderGroups.map(g => (<div key={g.group} className="strategy-group"><h4 className="strategy-group-label">{g.group}</h4>
        <div className="strategy-sliders">
          {g.items.map(s => (<div key={s.key} className="strategy-slider-row">
            <div className="slider-header"><span className="slider-label">{s.label}</span><span className="slider-value">{strategy[s.key as keyof StrategySettings]??50}</span></div>
            <div className="slider-desc">{s.desc}</div>
            <div className="slider-track-wrapper">
              <span className="slider-endpoint">{s.left}</span>
              <input type="range" min="0" max="100" value={strategy[s.key as keyof StrategySettings]??50}
                onChange={e=>setStrategy(prev=>({...prev,[s.key]:parseInt(e.target.value)}))}
                className="strategy-range"/>
              <span className="slider-endpoint">{s.right}</span>
            </div>
          </div>))}
        </div>
      </div>))}
      <button className="save-strategy-btn" onClick={async ()=>{
        if (!team||!isSupabaseConfigured()) return;
        const userId=userData?.id;
        if (!userId||team.owner_id!==userId) {alert('You can only edit strategy for teams you own.');return;}
        await supabase.from('teams').update({strategy}).eq('id',teamId).eq('owner_id',userId);
        setTeam(prev=>prev?{...prev,strategy}:null);
        alert('Strategy saved!');
      }}>Save Strategy</button>
    </div>);
  };

  const renderUniforms = () => {
    const pinstripe = uniform.style === 'pinstripe';
    const gradient = uniform.style === 'gradient';
    const vNeck = uniform.style === 'v-neck';
    return (<div className="uniforms-tab">
      <h3>Uniform Customizer</h3>
      <p className="section-note">Design your team's look.</p>
      <div className="uniform-preview">
        <div className="uniform-jersey" style={{
          background: gradient?`linear-gradient(180deg,${uniform.primaryColor},${uniform.secondaryColor})`:uniform.primaryColor,
          borderColor: uniform.secondaryColor,
        }}>
          {pinstripe&&<div className="pinstripe-overlay" style={{backgroundImage:`repeating-linear-gradient(90deg,transparent,transparent 8px,${uniform.secondaryColor}22 8px,${uniform.secondaryColor}22 9px)`}}/>}
          {vNeck&&<div className="vneck-overlay" style={{borderColor:uniform.secondaryColor}}/>}
          <div className="jersey-number" style={{color:uniform.secondaryColor}}>{uniform.jerseyNumber}</div>
        </div>
        <div className="uniform-cap" style={{background:uniform.capColor}}>
          <span className="cap-abbr">{team?.abbreviation||'TM'}</span>
        </div>
      </div>
      <div className="uniform-section"><h4>Jersey Style</h4>
        <div className="style-options">
          {UNIFORM_STYLES.map(s=>(<button key={s.value} className={`style-option ${uniform.style===s.value?'active':''}`}
            onClick={()=>setUniform(prev=>({...prev,style:s.value as any}))}>
            <span className="style-label">{s.label}</span><span className="style-desc">{s.desc}</span>
          </button>))}
        </div>
      </div>
      {[['Primary Color','primaryColor'],['Secondary Color','secondaryColor'],['Cap Color','capColor']].map(([label,key])=>(
        <div key={key} className="uniform-section"><h4>{label}</h4>
          <div className="color-swatches">
            {TEAM_COLORS.map(c=>(<button key={c} className={`swatch ${(uniform as any)[key]===c?'active':''}`}
              style={{background:c}} onClick={()=>setUniform(prev=>({...prev,[key]:c}))}/>))}
            <label className="swatch custom-swatch">
              <input type="color" value={(uniform as any)[key]} onChange={e=>setUniform(prev=>({...prev,[key]:e.target.value}))}/><span>+</span>
            </label>
          </div>
        </div>
      ))}
      <button className="save-strategy-btn" onClick={async ()=>{
        if (!team||!isSupabaseConfigured()) return;
        await supabase.from('teams').update({
          primary_color: uniform.primaryColor,
          secondary_color: uniform.secondaryColor,
          uniform_style: uniform.style,
        }).eq('id',teamId);
        alert('Uniform saved!');
        setTeam(prev=>prev?{...prev,primary_color:uniform.primaryColor,secondary_color:uniform.secondaryColor,uniform_style:uniform.style}:null);
      }}>Save Uniform</button>
    </div>);
  };

  // ──────────── Main render ────────────
  if (loading) return <div className="my-teams-page"><div className="loading">Loading...</div></div>;

  const teamDisplayName = team ? (
    (team.city && team.name && !team.name.toLowerCase().startsWith(team.city.toLowerCase()))
      ? `${team.city} ${team.name}` : team.name
  ) : 'Team';

  const tabs: [TeamTab, string][] = [
    ['roster', 'Roster'],
    ['lineup', 'Lineup'],
    ['depth', 'Depth Chart'],
    ['playerStats', 'Player Stats'],
    ['schedule', 'Schedule'],
    ['strategy', 'Strategy'],
    ['uniforms', 'Uniforms'],
    ['draft-sheet', 'Draft Sheet'],
    ['fa', 'Free Agency'],
  ];

  return (
    <div className="my-teams-page">
      {/* Back & header */}
      <button className="back-btn" onClick={() => {
        if (leagueId) onNavigate?.(`/league/${leagueId}`);
      }}>← Back to League</button>

      <div className="team-header-card">
        <div className="team-header-abbr" style={{
          background: team?.primary_color || 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
        }}>{team?.abbreviation || '??'}</div>
        <div className="team-header-info">
          <h1>{teamDisplayName}</h1>
          <div className="team-header-meta">
            <span>{teamStanding ? `${teamStanding.wins}-${teamStanding.losses}` : `${team?.wins||0}-${team?.losses||0}`}</span>
            {leagueInfo && <span style={{marginLeft:8,fontSize:'0.8rem',color:'#9ca3af'}}>{leagueInfo.name}</span>}
          </div>
        </div>
      </div>

      <div className="team-tabs">
        {tabs.map(([key, label]) => (
          <button key={key} className={`team-tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </div>

      <div className="team-tab-content">
        {activeTab === 'roster' && renderRoster()}
        {activeTab === 'lineup' && renderLineup()}
        {activeTab === 'depth' && renderDepthChart()}
        {activeTab === 'playerStats' && renderPlayerStats()}
        {activeTab === 'schedule' && renderSchedule2()}
        {activeTab === 'strategy' && renderStrategy()}
        {activeTab === 'uniforms' && renderUniforms()}
        {activeTab === 'draft-sheet' && leagueId && teamId && (
          <DraftSheetPage
            leagueId={leagueId}
            teamId={teamId}
            onBack={() => setActiveTab('roster')}
            onNavigate={(route) => onNavigate?.(route)}
          />
        )}
        {activeTab === 'fa' && leagueId && teamId && (
          <FreeAgencyPage
            leagueId={leagueId}
            teamId={teamId}
            onBack={() => setActiveTab('roster')}
          />
        )}
      </div>

      {boxscoreGameId && team?.league_id && (
        <BoxScore gameId={boxscoreGameId} leagueId={team.league_id} onClose={() => setBoxscoreGameId(null)} />
      )}
    </div>
  );
};

export default QuickLeagueTeamPage;